/**
 * `/share/:token` — the stats a partner admin shares with someone outside the
 * portal (Dashboard → "Share your stats", FR-011, US4). No login: the token is
 * the credential. It shows the funnel for the scope the partner chose when
 * making the link (D-3: last 7/30/90 days, optionally one website and/or one
 * trade) — counts only — says what that scope is, who shared it and when the
 * link expires. The viewer has no controls: the scope is stored on the link
 * and the API ignores any query string. Invalid, expired and revoked links all
 * look the same.
 *
 * Owner feedback (2026-09-27, "share only link loads messed up"):
 *   * the page loads as a skeleton of the final layout (same top bar, header,
 *     tiles, cards and footer), not a lone eyebrow and a centred "Loading…"
 *     that then jumps;
 *   * the scope is a line of plain facts, not pill chips that looked
 *     clickable;
 *   * it says whose stats these are and what the page is, and ends with
 *     "Shared by <org> · Powered by FinderlyFix" and a link to finderlyfix.com;
 *   * Outcomes lists each outcome with its share instead of a one-line legend
 *     floating in an empty card.
 * Still PII-free: the API sends aggregate counts and the org's display name only.
 */
import { Fragment, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import { ErrorNotice } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { FunnelBars, OutcomeSplit, UnmatchedSummary } from '../screens/Dashboard';
import { tradeLabel } from '../screens/ShareLinks';

const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 1000) / 10}%`);
const nf = new Intl.NumberFormat('en-US');
export const FINDERLYFIX_HOME = 'https://finderlyfix.com';

/** "Aug 28" (or "Aug 28, 2025" outside the current year) — a period needs no clock time. */
function day(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** The frame every state shares, so loading, loaded and invalid never shift the page. */
function Frame({ children, sharedBy, busy = false }: { children: ReactNode; sharedBy?: string; busy?: boolean }) {
  return (
    <main className="shared-dashboard" id="main" aria-busy={busy || undefined}>
      <div className="share-top">
        <a className="share-brand" href={FINDERLYFIX_HOME}>
          FinderlyFix
        </a>
        <span className="small muted">Shared stats · read-only</span>
      </div>
      {children}
      <footer className="share-foot small muted">
        {sharedBy ? <>Shared by {sharedBy} · </> : null}
        Powered by <a href={FINDERLYFIX_HOME}>FinderlyFix</a>
      </footer>
    </main>
  );
}

const Bar = ({ w, h = 14 }: { w: string; h?: number }) => <span className="skel" style={{ width: w, height: h }} />;

/** The loaded layout's shape in grey: header, four tiles, Funnel and Outcomes. */
function Skeleton() {
  return (
    <div className="stack-lg" aria-hidden="true">
      <header className="stack-sm">
        <Bar w="min(320px, 70%)" h={32} />
        <Bar w="min(560px, 100%)" />
        <Bar w="min(420px, 85%)" />
        <Bar w="140px" h={12} />
      </header>
      <div className="metrics">
        {[0, 1, 2, 3].map((i) => (
          <div className="metric stack-sm" key={i}>
            <Bar w="60%" h={28} />
            <Bar w="80%" h={12} />
          </div>
        ))}
      </div>
      <div className="grid-2 share-cards">
        {['Funnel', 'Outcomes'].map((name, c) => (
          <section className="panel" key={name}>
            <div className="panel-head">
              <h2>{name}</h2>
            </div>
            <div className="panel-body stack-sm">
              {Array.from({ length: c === 0 ? 7 : 5 }, (_, i) => (
                <Bar key={i} w={c === 0 ? `${100 - i * 9}%` : '100%'} h={c === 0 ? 22 : 16} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function SharedDashboard({ token }: { token: string }) {
  const { api } = useSession();
  const shared = useQuery(() => api.getSharedDashboard(token), [token]);
  const data = shared.data;

  if (shared.loading) {
    return (
      <Frame busy>
        <span className="visually-hidden" role="status">
          Loading shared stats…
        </span>
        <Skeleton />
      </Frame>
    );
  }
  if (shared.error) {
    return (
      <Frame>
        <h1 id="page-title" tabIndex={-1}>
          Shared stats
        </h1>
        <ErrorNotice error={shared.error} />
      </Frame>
    );
  }
  if (!data || !data.valid) {
    return (
      <Frame>
        <div className="stack-sm">
          <h1 id="page-title" tabIndex={-1}>
            This link is no longer valid
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            It may have expired or been revoked. Ask whoever shared it for a new one.
          </p>
        </div>
      </Frame>
    );
  }

  // Servers before migration 065 send no scope; their links showed 30 days, unfiltered.
  const scope = data.scope ?? { period_days: 30, origin: null, trade: null };
  const org = data.partner.display_name;
  const f = data.funnel;
  const facts = [
    `Last ${scope.period_days} days (${day(f.period.from)} – ${day(f.period.to)})`,
    scope.origin ? `Website: ${scope.origin.replace(/^https?:\/\//, '')}` : 'All websites',
    scope.trade ? `Trade: ${tradeLabel(scope.trade)}` : 'All trades',
    'Live leads only',
  ];

  return (
    <Frame sharedBy={org}>
      <div className="stack-lg">
        <header className="stack-sm">
          <h1 id="page-title" tabIndex={-1}>
            {org}
          </h1>
          <p className="lede" style={{ margin: 0 }}>
            Stats shared by {org}: how homeowners used their FinderlyFix repair-help widget. Counts only — no names or contact details.
          </p>
          {/* Plain facts, not chips: nothing here is a control. */}
          <p className="share-facts small" aria-label="What these stats cover">
            {facts.map((fact, i) => (
              <Fragment key={fact}>
                {i > 0 ? <span aria-hidden="true"> · </span> : null}
                <span className="fact">{fact}</span>
              </Fragment>
            ))}
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            This link expires {day(data.expires_at)}.
          </p>
        </header>
        <div className="metrics">
          <div className="metric">
            <div className="v">{nf.format(f.impressions)}</div>
            <div className="k">Widget impressions</div>
          </div>
          <div className="metric">
            <div className="v">{nf.format(f.captures)}</div>
            <div className="k">Leads captured</div>
          </div>
          <div className="metric">
            <div className="v">{pct(f.deflection_rate)}</div>
            <div className="k">Deflection rate</div>
          </div>
          <div className="metric">
            <div className="v">{pct(f.false_deflection_rate)}</div>
            <div className="k">False deflection</div>
          </div>
        </div>
        <div className="grid-2 share-cards">
          <section className="panel">
            <div className="panel-head">
              <h2>Funnel</h2>
              <span className="small muted">last {scope.period_days} days</span>
            </div>
            <div className="panel-body">
              <FunnelBars f={f} />
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <h2>Outcomes</h2>
              <span className="small muted">what homeowners did</span>
            </div>
            <div className="panel-body">
              <OutcomeSplit f={f} list />
            </div>
          </section>
        </div>
        <UnmatchedSummary f={f} fixes={false} />
      </div>
    </Frame>
  );
}
